import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import { getActiveCurrency } from '../Shared/Formatters';

/**
 * AnalyticsChart — Sprint 3.7. Renders the active metric series as Bar /
 * Line / Area / Pie. Click a bar/segment fires onBucketClick(rowIndex, row).
 */

const PALETTE = ['#3B82F6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#84CC16'];

export default function AnalyticsChart({
    chartType,
    rows,
    compareRows,
    groupBy,
    activeMetric,
    metricsFlags,
    onBucketClick,
}) {
    const { t } = useTranslation();
    const chartRef = useRef(null);

    const { series, options } = useMemo(
        () => buildChart({ chartType, rows, compareRows, groupBy, activeMetric, metricsFlags, onBucketClick, t }),
        [chartType, rows, compareRows, groupBy, activeMetric, metricsFlags, onBucketClick, t]
    );

    const apexType = chartType === 'area' ? 'area'
        : chartType === 'line' ? 'line'
        : chartType === 'pie'  ? 'pie'
        : 'bar';

    return (
        <div className="v3-card v3-anlx-chart-shell" ref={chartRef}>
            {!rows || rows.length === 0 ? (
                <div className="v3-anlx-chart-empty">
                    {t('No data for the current filters.')}
                </div>
            ) : (
                <Chart
                    key={apexType}
                    options={options}
                    series={series}
                    type={apexType}
                    height={380}
                />
            )}
        </div>
    );
}

function buildChart({ chartType, rows, compareRows, groupBy, activeMetric, metricsFlags, onBucketClick, t }) {
    const labels = (rows || []).map(r => r.groupByValue || t('N/A'));
    const metricKey = activeMetric || firstActiveMetric(metricsFlags) || 'sales';
    const metricLabel = t(METRIC_LABELS[metricKey] || metricKey);
    const values = (rows || []).map(r => Number(r[metricKey]) || 0);

    // Compare series: align with current rows.
    //  - groupBy=date  → align by index (Day 1 of A vs Day 1 of B), since the
    //    date strings differ between the two ranges.
    //  - other groupBy → align by groupByValue match (tenant/category/etc.).
    let compareValues = null;
    if (Array.isArray(compareRows) && compareRows.length) {
        if (groupBy === 'date') {
            compareValues = (rows || []).map((_, i) => {
                const r = compareRows[i];
                return r ? Number(r[metricKey]) || 0 : 0;
            });
        } else {
            const map = new Map(compareRows.map(r => [String(r.groupByValue || ''), Number(r[metricKey]) || 0]));
            compareValues = (rows || []).map(r => map.get(String(r.groupByValue || '')) || 0);
        }
    }

    const baseOptions = {
        chart: {
            id: 'v3-anlx-chart',
            background: 'transparent',
            foreColor: 'var(--v3-text-secondary)',
            toolbar: { show: false },
            events: {
                dataPointSelection: (_e, _ctx, cfg) => {
                    const idx = cfg?.dataPointIndex ?? -1;
                    if (idx >= 0 && rows && rows[idx] && typeof onBucketClick === 'function') {
                        onBucketClick(idx, rows[idx]);
                    }
                },
            },
        },
        colors: PALETTE,
        grid: { borderColor: 'rgba(255,255,255,0.06)', strokeDashArray: 3 },
        tooltip: { theme: 'dark', y: { formatter: (v) => formatTooltip(v, metricKey) } },
        legend: { labels: { colors: 'var(--v3-text-secondary)' } },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: chartType === 'line' || chartType === 'area' ? 2 : 0 },
    };

    if (chartType === 'pie') {
        // Pie ignores compare overlay (no meaningful overlay representation).
        return {
            series: values,
            options: {
                ...baseOptions,
                labels,
                legend: { ...baseOptions.legend, position: 'right' },
                tooltip: { ...baseOptions.tooltip, y: { formatter: (v) => formatTooltip(v, metricKey) } },
            },
        };
    }

    const series = compareValues
        ? [
            { name: t('{{metric}} (current)', { metric: metricLabel }), data: values },
            { name: t('{{metric}} (compare)', { metric: metricLabel }), data: compareValues },
          ]
        : [{ name: metricLabel, data: values }];

    return {
        series,
        options: {
            ...baseOptions,
            xaxis: {
                categories: labels,
                labels: { style: { colors: 'var(--v3-text-muted)' }, rotate: -30, rotateAlways: false, maxHeight: 60 },
                axisBorder: { color: 'rgba(255,255,255,0.08)' },
                axisTicks:  { color: 'rgba(255,255,255,0.08)' },
            },
            yaxis: {
                labels: {
                    style: { colors: 'var(--v3-text-muted)' },
                    formatter: (v) => formatYAxis(v, metricKey),
                },
            },
            fill: chartType === 'area'
                ? { type: 'gradient', gradient: { shadeIntensity: 0.6, opacityFrom: 0.55, opacityTo: 0.05 } }
                : { opacity: 1 },
            // Always include plotOptions.bar — ApexCharts reads `plotOptions.bar`
            // internally even when type is line/area/pie, and crashes if it's
            // undefined when switching chart types on the same instance.
            plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
            markers: chartType === 'line' || chartType === 'area'
                ? { size: 4, strokeWidth: 0, hover: { sizeOffset: 2 } }
                : { size: 0 },
        },
    };
}

const METRIC_LABELS = {
    sales: 'Sales',
    transactionCount: 'Transactions',
    atv: 'ATV',
    revenueShareAmount: 'Revenue Share',
    revenueSharePercentage: 'Revenue Share %',
    baseRent: 'Base Rent',
};

function firstActiveMetric(flags) {
    if (!flags) return null;
    if (flags.sales)              return 'sales';
    if (flags.transCount)         return 'transactionCount';
    if (flags.atv)                return 'atv';
    if (flags.revenueShareAmount) return 'revenueShareAmount';
    if (flags.baseRent)           return 'baseRent';
    return null;
}

function formatTooltip(v, kind) {
    if (!Number.isFinite(v)) return '—';
    if (kind === 'transactionCount') return Math.round(v).toLocaleString();
    // Every other metric (sales, atv, revenueShareAmount, baseRent) is money —
    // prefix the tenant's display currency instead of a bare number.
    return `${getActiveCurrency()} ${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}
function formatYAxis(v, kind) {
    if (!Number.isFinite(v)) return '';
    if (kind === 'transactionCount') return Math.round(v).toLocaleString();
    const cur = getActiveCurrency();
    if (Math.abs(v) >= 1_000_000) return `${cur} ${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 1_000)     return `${cur} ${(v / 1_000).toFixed(1)}K`;
    return `${cur} ${v.toFixed(0)}`;
}
